import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IRunMethodResult } from "@multiweb/types/contracts";
import { InvalidArgumentException, InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { IItemRights, IItemRightsCollection, RightType } from "../../../../../Global/Base/Rights/RightTypes";
import { getClassItemsRights } from "../../../../../Global/Base/Rights/utils";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { ClassItemType, IClassItem, IClassItemRights as ClassItemRights } from "../../../../../OW.Remoting/Remoting/Rights";
import { IEnableInEditControl } from "../../../../UI.BaseControls/ControlInterfaces";
import { ActionContext, ActionType, IActionContext, SelectionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { ComponentRightsExtractor } from "../../../Rights/ComponentRightsExtractor";
import { Rights } from "../../../Rights/Rights";
import {
    ControlParams,
    ICloseFormControl,
    IConfirmIfControl,
    IInfoProvidingControl,
    IMethodControl,
    IRunWithoutEdit,
    IStaticControl,
    IValidateIfControl,
    IValidateRecommendedControl,
} from "../../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageButtonType, MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { IControlInfoObject } from "../ControlInfo";
import { IValidationMaster } from "../FormControl";
import { IMasterableFrameWithDataGrid } from "../Frames/FrameInterfaces";
import { FrameSharedMethods } from "../Frames/FrameSharedMethods";
import { IDetailFrame } from "../Frames/IDetailMasterableFrame";
import { MasterableFrame } from "../Frames/MasterableFrame";
import { WindowedDetailFrame } from "../WindowedDetailFrame";
import { ActionSharedMethods } from "./ActionSharedMethods";
import { XGAction } from "./XGAction";
import { IFrame } from "../IFrame";

const logger = LazyLoadingSectionLogger.create(LoggingSection.methodAction);

export class MethodAction extends XGAction implements IMethodControl, IStaticControl, ICloseFormControl, IConfirmIfControl, IValidateIfControl, IEnableInEditControl, IInfoProvidingControl, IRunWithoutEdit, IValidateRecommendedControl {
    //#region Fields
    /**
     * Název spouštěné metody. U statické metody ve tvaru Trida.Metoda.
     */
    public readonly methodName: string;

    /**
     * Příznak, zda se po vykonání metody má obnovit seznam
     */
    public readonly refreshList: boolean;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat doporučené atributy.
     */
    public readonly validateRecommended: boolean;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat odložené povinné atributy
     */
    public readonly validatePostReq: boolean;

    /**
     * Lokalizační klíč textu hlášky, která se zobrazí v LoadingLayeru
     */
    public readonly loaderTextLocKey: string;

    public readonly confirmLockey: string;

    /**
     * Udava jestli je metoda staticka
     */
    public readonly isStatic: boolean;

    protected _rightsDefinition: IItemRightsCollection;
    protected _cachedClassItemRights: ClassItemRights;

    protected _closeForm: boolean = false;
    protected _confirmIf: boolean = false;
    protected _validateIf: boolean = false;

    private _runWithoutEdit: boolean = false;
    //#endregion Fields

    //#region Properties
    public get runWithoutEdit(): boolean {
        return this._runWithoutEdit;
    }

    public override get context(): ActionContext {
        if (this.isStatic || this.useMasterObject)
            return ActionContext.frameAction;
        else
            return ActionContext.objectAction;
    }

    public override get supportedSelectionModes(): SelectionType {
        return SelectionType.all; // akce je dostupná při vybraném jednom záznamu i při vybrání více záznamů (u statické je to jedno a u instanční umíme hromadné spuštění)
    }

    public override get actionType(): ActionType {
        return ActionType.method;
    }

    public get closeForm(): boolean {
        return this._closeForm;
    }

    public set closeForm(value: boolean) {
        this._closeForm = value;
    }

    public get confirmIf(): boolean {
        return this._confirmIf;
    }

    public set confirmIf(value: boolean) {
        this._confirmIf = value;
    }

    public get validateIf(): boolean {
        return this._validateIf;
    }

    public set validateIf(value: boolean) {
        this._validateIf = value;
    }
    //#endregion Properties

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("MethodAction");
        this.setToMenu(MenuType.mtActions);
        this.methodName = this.element.getAttribute("data-methname");
        this.refreshList = this.getBoolDataAttr("refreshlist");
        this.confirmLockey = this.element.getAttribute("data-confirm");
        if (this.confirmLockey != null)
            this.confirmLockey = "XG-" + this.confirmLockey;
        this.isStatic = this.getBoolDataAttr("isstatic");
        this.validatePostReq = this.getBoolDataAttr("validatepostreq");
        this.validateRecommended = this.getBoolDataAttr("validaterecommended");
        this._runWithoutEdit = this.getBoolDataAttr("runwithoutedit", this.isStatic ? true : false);
        this._rightsDefinition = ComponentRightsExtractor.loadRightsForTypes(this, RightType.validateIf | RightType.closeForm | RightType.confirmIf);
    }

    //#region Methods
    protected _validateMethodName(): void {
        if (!this.methodName) {
            logger.error("Property MethName must be specified");
            return;
        }

        if (!this.isStatic && this.methodName.indexOf(".") !== -1)
            logger.error(`Name of non-static method has a class name which is not valid (${this.methodName})`);
    }

    /**
     * Nastavuje přístupnost akce
     */
    public setReadOnly(): void {
        // Prázdná metoda
    }

    /** Spuštění pro ID objektu (nesmí používat instanci z framu) */
    protected async _confirmedExecuteWithoutInstance(context: IActionContext): Promise<void> {
        try {
            // Pokud je context.isSelectedAll === true, všechny záznamy se považují za vybrané, takže není potřeba ověřovat, zdali má parametr context alespoň jedno ID záznamu.
            if (!context || !context.className || (context.isSelectedAll ? false : !context.ids || context.ids.length === 0))
                throw new InvalidOperationException("Cannot execute MethodAction. IDs or className must be specified when invoking action for object without instance.");

            const { className, ids, isSelectedAll, condition, conditionParams } = context;

            UIManager.instance.showLoader();

            const enabledCondInfo = this._getRightConditionInfo(RightType.enabled);
            const visibleCondInfo = this._getRightConditionInfo(RightType.visible);

            let result: IRunMethodResult;
            if (!isSelectedAll && ids.length === 1) {
                result = await OWManager.runMethodForId({
                    id: ids[0],
                    className: context.className,
                    methodName: this.methodName,
                    requestContext: this._getRequestContext(),
                });
            } else if (isSelectedAll || ids.length > 1) {
                const objectList = new OWObjectList();
                objectList.isSelectAll = isSelectedAll;
                objectList.condition = condition;
                objectList.conditionParams = conditionParams;
                objectList.className = className;
                ids.forEach(id => objectList.add({ className, id }));

                result = await OWManager.runMethod({
                    formId: this.ownerForm.formName,
                    methodName: this.methodName,
                    objectList,
                    visibleCondId: visibleCondInfo.id,
                    enabledCondId: enabledCondInfo.id,
                    isVisibleCondNegative: visibleCondInfo.isNegative,
                    isEnabledCondNegative: enabledCondInfo.isNegative,
                    userLocKey: this._methodLocKey,
                    requestContext: this._getRequestContext(),
                });
            }
            await ActionSharedMethods.checkMethodResult(result, this._methodLocKey);

            await this._refreshData(result, this.executionContext.ownerFrame);

            UIManager.instance.showResultMessages(result.messages);
        } catch (e) {
            ActionSharedMethods.handleErrors(e, this.executionContext.actualObject, this.executionContext.ownerFrame);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    protected async _confirmedExecute(): Promise<void> {
        const { ownerFrame, actualObject } = this.executionContext;

        try {
            UIManager.instance.showLoader();
            if (!this.methodName || this.methodName.trim().length == 0) {
                throw Error("[MethodAction] Mandatory attribute MethodName is not set.");
            }

            let result: IRunMethodResult;

            if (this.isStatic) {
                const { objectName, methodName } = FrameSharedMethods.parseStaticMethod(ownerFrame, this.methodName);

                if (objectName == null) {
                    logger.error(`Unable to run static method '${this.methodName}' - objectName is unavailable`);
                    return;
                }

                result = await OWManager.runStaticMethod({
                    className: objectName,
                    methodName,
                    userLocKey: this._methodLocKey,
                    formId: this.ownerForm.formName,
                    requestContext: this._getRequestContext(),
                });

                await ActionSharedMethods.checkMethodResult(result, this._methodLocKey);

                if (ownerFrame && !this.enableInEdit && this.refreshList)
                    await ownerFrame.refreshData(false);

            } else {
                const isMultiSelectingMode = ownerFrame instanceof MasterableFrame && ownerFrame.isMultiSelectingMode;
                if (!isMultiSelectingMode && actualObject == null) {
                    logger.error(`Unable to run instance method '${this.methodName}' - instance of object unavailable`);
                    return;
                }

                if (isMultiSelectingMode) {
                    if (ownerFrame instanceof IMasterableFrameWithDataGrid) {
                        if (!ownerFrame.currentDataGrid)
                            throw new InvalidOperationException("Cannot execute MethodAction. Owner frame must have a DataGrid.");
                        if (!ownerFrame.currentDataGrid.selectedRows || ownerFrame.currentDataGrid.selectedRows.length === 0)
                            throw new InvalidOperationException("Cannot execute MethodAction. There are no objects selected.");

                        const objectName = ownerFrame.currentDataGrid.objectName;
                        const objectList = new OWObjectList();
                        objectList.isSelectAll = ownerFrame.currentDataGrid.isSelectedAll;
                        if (objectList.isSelectAll) {
                            objectList.condition = ownerFrame.currentDataGrid.dataSource.condition;
                            objectList.conditionParams = [...ownerFrame.currentDataGrid.dataSource.conditionParams];
                            objectList.className = objectName;
                            ownerFrame.currentDataGrid.dataRows.filter(r => !(ownerFrame as IMasterableFrameWithDataGrid).currentDataGrid.selectedRows.includes(r)).forEach(row => objectList.add({ id: row.dataKey, className: objectName }));
                        } else {
                            ownerFrame.currentDataGrid.selectedRows.forEach(row => objectList.add({ id: row.dataKey, className: objectName }));
                        }

                        const enabledCondInfo = this._getRightConditionInfo(RightType.enabled);
                        const visibleCondInfo = this._getRightConditionInfo(RightType.visible);

                        result = await OWManager.runMethod({
                            formId: this.ownerForm.formName,
                            methodName: this.methodName,
                            objectList,
                            visibleCondId: visibleCondInfo.id,
                            enabledCondId: enabledCondInfo.id,
                            isVisibleCondNegative: visibleCondInfo.isNegative,
                            isEnabledCondNegative: enabledCondInfo.isNegative,
                            userLocKey: this._methodLocKey,
                            requestContext: this._getRequestContext(),
                        });
                    } else {
                        throw new InvalidArgumentException("Cannot execute MethodAction. Frame must implement IMasterableFrameWithDataGrid in order to retrieve selected objects.");
                    }
                } else {
                    result = await actualObject.run({
                        methodName: this.methodName,
                        userLocKey: this._methodLocKey,
                        formId: this.ownerForm.formName,
                        requestContext: this._getRequestContext(),
                    });
                }
                await ActionSharedMethods.checkMethodResult(result, this._methodLocKey);

                await this._refreshData(result, ownerFrame, actualObject, isMultiSelectingMode);
            }

            await this._evaluateCloseFormIfAndCloseForm();

            UIManager.instance.showResultMessages(result.messages);
        } catch (e) {
            ActionSharedMethods.handleErrors(e, actualObject, ownerFrame);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    // získá id a negaci podmínky pro určený typ práv
    private _getRightConditionInfo(rightType: RightType): { id: string; isNegative: boolean } {
        const right = ComponentRightsExtractor.loadRightsForTypes(this, rightType);
        const rightName = RightType[rightType] as keyof IItemRightsCollection;

        const id = right[rightName] ? (right[rightName] as IItemRights[])[0]?.conditionID : null;
        const isNegative = right[rightName] ? (right[rightName] as IItemRights[])[0]?.isNegative : null;

        return { id, isNegative };
    }

    private async _refreshData(runMethodResult: IRunMethodResult, ownerFrame: IFrame, actualObject?: OWObject, isMultiSelectingMode: boolean = false): Promise<void> {
        if (runMethodResult?.isInLongTransaction) {
            return;
        }
        if (!ownerFrame) {
            FrameSharedMethods.refreshFrameAfterSetActive(this.ownerForm);
            return;
        }
        if (ownerFrame instanceof MasterableFrame && ownerFrame.masterRefresh) { // Pokud má owner nastaveno masterrefresh, tak udělá bez ohledu na nastavení refreshList nebo isMultiselectingMode, protoře refresh data se provede v ramci MasterRefresh
            await ownerFrame.refreshActualObject();
            return;
        }
        if (this.refreshList || isMultiSelectingMode) {
            await ownerFrame.refreshData(true);
            return;
        }
        if (actualObject && (!(ownerFrame instanceof IDetailFrame) || (ownerFrame instanceof WindowedDetailFrame)) && !this.enableInEdit) {
            /* Pro DF (a SDF) by se nemělo volat - všechny změny se zjistí přes WS a zde by byl refresh duplicitní.
            * TODO: [JN] asi by se mělo odstranit pro všechny framy, ale nedokážu říct, jestli to bude fungovat pro všechny případy (muselo by se to předělat jako na SDF - poslouchání OWObject.dataRefreshed)
            * I když WindowedDetailedFrame implementuje interface IDetailFrame, je potřeba provést refresh, protože nemá mastera odkud by na něj změna dostala.*/
            await ownerFrame.refreshActualObject();
        }
    }

    private get _methodLocKey(): string {
        if (this.locKey)
            return this.locKey;
        if (this.toolbarButton?.locKey)
            return this.toolbarButton.locKey;
        return undefined;
    }

    //TODO-rites: přepsat, aby reflektovalo field/property CloseForm
    protected async _evaluateCloseFormIfAndCloseForm(): Promise<void> {
        if (this._rightsDefinition.closeForm) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._rightsDefinition, this._rightsDefinition.closeForm, this.executionContext.actualObject);
            if (rightResult.closeForm) {
                FrameSharedMethods.refreshFrameAfterSetActive(this.owner.ownerForm);
                await this.ownerForm.close();
            }
        }
    }

    //TODO-rites: přepsat, aby reflektovalo field/property validateIf
    private async _evaluateValidateIf(): Promise<boolean> {
        if (this._rightsDefinition.validateIf) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._rightsDefinition, this._rightsDefinition.validateIf, this.executionContext.actualObject);
            return rightResult.validate;
        }
        return false;
    }

    //TODO-rites: přepsat, aby reflektovalo field/property ConfirmIf
    protected async _evaluateConfirmIf(): Promise<boolean> {
        if (this._rightsDefinition.confirmIf) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._rightsDefinition, this._rightsDefinition.confirmIf, this.executionContext.actualObject);
            return rightResult.confirm;
        }
        return true;
    }

    protected async _checkConfirmCondition(skipConfirmIf: boolean = false): Promise<boolean> {
        if (this.confirmLockey && (skipConfirmIf || await this._evaluateConfirmIf())) {
            const value = await MessageWindow.showYesNoDialog(new LocalizedStringBuilder(this.confirmLockey));
            if (value.result == MessageButtonType.no) {
                return false;
            }
        }
        return true;
    }

    /**
     * Vstupní bod pro spuštění provedení metody
     * @param context ID a třída objektu, pro který má být akce provedena
     */
    protected override async _internalExecute(context?: IActionContext): Promise<void> {
        try {
            if (!this.methodName || this.methodName.trim().length == 0) {
                throw Error("[MethodAction] Mandatory attribute MethodName is not set.");
            }

            if (context) { //akce nesmí využívat instanci objektu
                if (!(await this._checkConfirmCondition(true))) {
                    return;
                }
                await this._confirmedExecuteWithoutInstance(context);
            } else {
                const { ownerFrame } = this.executionContext;
                // zkontroluje se uložení změn na owneru a jeho nadřízených framech
                if (!(await this._checkAndSaveForm({ cancelEditIfNotEdited: true, checkRecommended: this.validateRecommended, checkPostponed: this.validatePostReq }))) {
                    return;
                }

                // Počká se na aktualizaci objektu, aby se metoda spustila nad správnými daty
                if (ownerFrame) {
                    await ownerFrame.waitForActualObjectSet(); // Po uložení se spouští nový set objektu a je třeba na něj počkat

                    if (this.executionContext.actualObject) {
                        await this.executionContext.actualObject.waitForUpdateInstance();
                    }
                }

                if (ownerFrame instanceof IValidationMaster && await this._evaluateValidateIf()) {
                    const result = await ownerFrame.validateAndShowMessage();
                    if (!result)
                        return;
                }

                if (!(await this._checkConfirmCondition()))
                    return;

                if (this.isStatic || await this._checkRights(this.validateRecommended, this.validatePostReq)) //je potřebna kontrola vyplnění odložené povinných a/nebo doporučených polí
                    await this._confirmedExecute();
            }
        } catch (e) {
            ActionSharedMethods.handleErrors(e, this.executionContext.actualObject, this.executionContext.ownerFrame);
        }
    }

    /**
     * Získá ze serveru práva na statickou metodu
     * @param ct CancellationToken
     */
    protected async _getItemRight(ct?: CancellationToken): Promise<ClassItemRights> {
        if (this._cachedClassItemRights)
            return this._cachedClassItemRights;

        const classItemsRightsToEvaulate = new Array<IClassItem>();
        let name = this.methodName;
        if (name.indexOf(".") === -1) // statické metody mají před tečkou uvedený název třídy, který může být jiný, než má owner frame
            name = this.executionContext.ownerFrame.objectName + "." + name;

        classItemsRightsToEvaulate.push({ name: name, type: ClassItemType.Method });

        const data = await getClassItemsRights(null, classItemsRightsToEvaulate, this._getRequestContext(ct), true);
        const aktItemRight: ClassItemRights = data.get(name);
        this._cachedClassItemRights = aktItemRight;

        return aktItemRight;
    }

    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>): Promise<Partial<IControlInfoObject>> {
        return {
            ...childInfo,
            locKey: this.locKey,
            additionalInfo: {
                dataAttributes: { ...this._getDataAttributes() },
                implementationClass: this.constructor.name,
            },
        };
    }

    //#endregion Methods
}
